import { Clipboard } from '@angular/cdk/clipboard';
import { Dialog } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TippyDirective } from '@ngneat/helipopper';
import {
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  tap
} from 'rxjs';

import { PHRASES } from '@app/modules/bets/dialogs/autobet-preset/notification-phrases';
import { catchCaptcha } from '@app/modules/captcha';
import { UserConditionsDialog } from '@app/modules/promocodes/dialogs/user-conditions/user-conditions.dialog';
import { UserCooldown } from '@app/modules/promocodes/promocodes.entity';
import { PromocodesState } from '@app/modules/promocodes/promocodes.state';
import { ServiceItem, ServiceItemOpenResult } from '@app/modules/service-items/service-items.entity';
import { getServiceItemDialog } from '@app/modules/service-items/utils';
import { AuthState } from '@app/modules/users';
import { formatNumber } from '@core/common/format-number';
import { NumberPipe } from '@core/common/pipes';
import { ConvertDirective } from '@core/converter';
import {
  addLeadingZero,
  countdown,
  indicateLoading,
  strToNumber
} from '@core/helpers';
import { Notifier } from '@core/notifications';
import { LoginDialog } from '@ui/dialogs';

import { UserCodeTypes } from '../../user-bonus.entity';
import { UserBonusState } from '../../user-bonus.state';

export const SECRET_CODE_APPLIED_MESSAGE = $localize`:@@payment.secret_code.success_apply:Секретный код применен!`;
export const PROMOCODE_APPLIED_MESSAGE = $localize`:@@bonuses.promo_applied:Промокод применен!`;

type PromocodesCountdown = {
  timer: string;
  progress: number;
};

@Component({
  selector: 'payment-user-bonus-secret-code',
  templateUrl: './user-bonus-secret-code.component.html',
  styleUrl: './user-bonus-secret-code.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ConvertDirective,
    TippyDirective,
    NumberPipe,
    AsyncPipe
  ]
})
export class UserBonusSecretCodeComponent implements OnInit {
  public readonly promocodesState = inject(PromocodesState);
  public readonly userBonusState = inject(UserBonusState);
  private readonly notifier = inject(Notifier);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(Dialog);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  public readonly authState = inject(AuthState);

  private readonly clipboard = inject(Clipboard);

  public readonly isApplying = signal<boolean>(false);
  public readonly isSuccessApplied = signal<boolean>(false);

  public userInputCodeControl = new FormControl('', [Validators.required]);

  public totalConditions = Object.keys(this.promocodesState?.userConditions()).length;

  constructor() {
    this.loadUserStatus();
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        filter((queryParams) => !!queryParams['secretCode']),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((queryParams) => {
        this.router.navigate(['.'], { relativeTo: this.route });
        this.userInputCodeControl.patchValue(queryParams['secretCode']);
        this.cdr.detectChanges();
      });
  }

  private loadUserStatus(): void {
    this.promocodesState
      .loadUserStatus()
      .pipe(indicateLoading(this.isApplying), takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public readonly countdown$ = toObservable(this.promocodesState.cooldown).pipe(
    filter((cooldown): cooldown is UserCooldown => !!cooldown),
    switchMap((cooldown) => {
      const secondsTotal = (Number(new Date(cooldown.endAt)) - Number(new Date(cooldown.startAt))) / 1000;

      return countdown(cooldown.endAt, { secondsTotal });
    }),
    map((cd) => {
      const hours = addLeadingZero(cd.duration!.hours(), false);
      const minutes = addLeadingZero(cd.duration!.minutes(), false);
      const seconds = addLeadingZero(cd.duration!.seconds(), false);

      return <PromocodesCountdown>{
        timer: `${hours}:${minutes}:${seconds}`,
        progress: cd.progress!
      };
    })
  );

  getConfirmedConditions(): number {
    const array = this.promocodesState.userConditions();

    return Object.values(array).reduce((prevValue, item) => {
      if (item) {
        return Number(prevValue) + Number(item);
      }

      return prevValue;
    }, 0);
  }

  usePromocode(): void {
    if (!this.authState.isAuthenticated()) {
      this.dialog.open(LoginDialog);

      return;
    }

    this.isSuccessApplied.set(false);

    this.promocodesState
      .usePromocode(this.userInputCodeControl.value!.trim())
      .pipe(
        indicateLoading(this.isApplying),
        catchCaptcha(this.dialog, () => this.usePromocode()),
        mergeMap((data) => {
          if (data.type === UserCodeTypes.SECRET_CODE) {
            return of(data).pipe(
              tap(() => {
                this.isSuccessApplied.set(true);
                this.notifier.success(SECRET_CODE_APPLIED_MESSAGE);
              }),
              switchMap(() => this.userBonusState.loadUserBonuses())
            );
          }

          return of(data).pipe(
            tap(() => {
              if (data.type === UserCodeTypes.PROMOCODE) {
                if (data?.result?.useId) {
                  this.isSuccessApplied.set(true);
                  this.notifier.success(PROMOCODE_APPLIED_MESSAGE);
                }

                if (Array.isArray(data?.result.use)) {
                  const matchingItem = data?.result.use.find(entry => entry?.serviceItem)?.serviceItem;

                  if (matchingItem && matchingItem.item && matchingItem.useResult) {
                    this.showServiceItemDialog(matchingItem.item, matchingItem.useResult);
                  }
                } else if (data.result?.serviceItem && data.result?.serviceItemUseResult) {
                  this.showServiceItemDialog(data.result?.serviceItem, data.result?.serviceItemUseResult);
                  this.isSuccessApplied.set(true);
                  this.notifier.success(PROMOCODE_APPLIED_MESSAGE);
                }
              }
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async showServiceItemDialog(serviceItem: ServiceItem, openResult: ServiceItemOpenResult): Promise<void> {
    const { type } = serviceItem;

    const dialogComponent = getServiceItemDialog(type);

    if (!dialogComponent) {
      return;
    }

    const dialogData = {
      openResult,
      serviceItem,
      inventoryItem: { id: openResult.invItem?.id },
      isHiddenButton: true,
      callback: () => {}
    };

    const cmp = await dialogComponent();

    this.dialog.open(cmp.default, {
      data: dialogData
    });
  }

  public readonly bonusUsd = computed(() => {
    const secretCode = this.userBonusState.secretCode();

    if (!secretCode || secretCode.bonusAmountUsd <= 0) {
      return '';
    }

    return formatNumber(strToNumber(secretCode.bonusAmountUsd));
  });

  private readonly secretCodeEffect = effect(() => {
    const currentSecretCode = this.userBonusState.secretCode();

    if (!currentSecretCode) {
      return;
    }

    this.secretCode = currentSecretCode.code;
    this.cdr.detectChanges();
  });

  public secretCode: string | undefined;

  getShortenSecretCode(): string {
    const secretCode = this.secretCode;

    if (!secretCode) {
      return '';
    }

    return `${secretCode.slice(0, 4)}...${secretCode.slice(secretCode.length - 4, secretCode.length)}`;
  }

  openConditions(): void {
    this.dialog.open(UserConditionsDialog);
  }

  copySecretCode(): void {
    const secretCode = this.secretCode;

    if (!secretCode) {
      return;
    }

    this.clipboard.copy(secretCode);
    this.notifier.success(PHRASES.CODE_COPIED);
  }
}
